function bindNativeCarouselDrag(track) {
  const DRAG_THRESHOLD = 5;
  let pointerId = null;
  let startX = 0;
  let startScroll = 0;
  let isDown = false;
  let didDrag = false;
  let suppressClick = false;

  const isInteractive = (target) =>
    !!target.closest(
      'button, input, select, textarea, label, .left-arrow, .right-arrow, .quick-add, .quick-add-lite_wrapper, .quickadd-variant'
    );

  const releaseCapture = (id) => {
    if (id === null || id === undefined) return;
    try {
      if (track.hasPointerCapture?.(id)) {
        track.releasePointerCapture(id);
      }
    } catch (err) {
      /* ignore */
    }
  };

  const endDrag = (e) => {
    if (!isDown) return;
    if (e && pointerId !== null && e.pointerId !== undefined && e.pointerId !== pointerId) {
      return;
    }

    const capturedId = pointerId;
    const wasDragging = didDrag;

    isDown = false;
    pointerId = null;
    didDrag = false;
    track.classList.remove('is-dragging');
    releaseCapture(capturedId);

    // Keep click suppression for this gesture only (click fires after pointerup)
    if (wasDragging) {
      suppressClick = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          suppressClick = false;
        });
      });
    }
  };

  track.addEventListener(
    'pointerdown',
    (e) => {
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      if (isInteractive(e.target)) return;

      // Clear any stuck previous gesture before starting a new one
      if (isDown) endDrag();

      isDown = true;
      didDrag = false;
      suppressClick = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
    },
    true
  );

  track.addEventListener(
    'pointermove',
    (e) => {
      if (!isDown || e.pointerId !== pointerId) return;

      const dx = e.clientX - startX;

      if (!didDrag && Math.abs(dx) >= DRAG_THRESHOLD) {
        didDrag = true;
        track.classList.add('is-dragging');
        try {
          track.setPointerCapture(pointerId);
        } catch (err) {
          /* ignore */
        }
      }

      if (!didDrag) return;

      e.preventDefault();
      track.scrollLeft = startScroll - dx;
      track.dispatchEvent(new Event('scroll'));
    },
    { passive: false, capture: true }
  );

  // Window-level release so mouseup outside the track always ends the drag
  window.addEventListener('pointerup', endDrag, true);
  window.addEventListener('pointercancel', endDrag, true);
  track.addEventListener('lostpointercapture', endDrag);

  // Block link navigation only when this gesture was actually a drag
  track.addEventListener(
    'click',
    (e) => {
      if (!suppressClick) return;
      e.preventDefault();
      e.stopPropagation();
      suppressClick = false;
    },
    true
  );

  // Prevent browser from starting a native link/image drag (kills our scroll drag)
  track.addEventListener(
    'dragstart',
    (e) => {
      e.preventDefault();
    },
    true
  );
}

function bindNativeCarouselProgress(track, carousel) {
  // Progress lives as a sibling just after the carousel (outside overflow:hidden .panels)
  const progress =
    carousel.parentElement?.querySelector(':scope > .product-carousel-progress') ||
    carousel.querySelector('.product-carousel-progress');
  if (!progress) return;

  const update = () => {
    if (window.matchMedia('(max-width: 1023px)').matches) {
      progress.hidden = true;
      return;
    }

    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 1) {
      progress.hidden = true;
      return;
    }

    progress.hidden = false;
    // Start at 10%, fill remaining 90% across the scroll range (min fill)
    const ratio = 0.1 + 0.9 * (track.scrollLeft / maxScroll);
    const pct = Math.min(100, Math.max(10, ratio * 100));
    progress.style.setProperty('--progress', pct.toFixed(2) + '%');
  };

  track.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });

  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(update);
    ro.observe(track);
    const list = track.querySelector('.splide__list');
    if (list) ro.observe(list);
  }

  requestAnimationFrame(update);
  setTimeout(update, 150);
  setTimeout(update, 600);
}

/**
 * Native free-scroll — ONLY for featured collection (.js-native-product-carousel).
 * Vertical page scroll is never intercepted; horizontal moves via native overflow + drag.
 */
function initNativeProductCarousels(root = document, options = {}) {
  root.querySelectorAll('.js-native-product-carousel.splide').forEach((el) => {
    if (options.skipDeferred && el.closest('[data-splide-defer]')) return;
    if (el.dataset.splideInitialized === 'true') return;

    const track = el.querySelector('.splide__track');
    const list = el.querySelector('.splide__list');
    if (!track || !list) return;
    if (list.querySelectorAll('.splide__slide').length === 0) return;

    el.dataset.splideInitialized = 'true';
    el.classList.add('is-initialized', 'is-rendered', 'product-carousel--native');
    bindNativeCarouselDrag(track);
    bindNativeCarouselProgress(track, el);
  });
}

/**
 * Classic Splide for other product rows without data-splide
 * (related products, recently viewed, etc.).
 */
function initClassicProductSplides(root = document, options = {}) {
  if (typeof Splide === 'undefined') return;

  root
    .querySelectorAll('.splide:not([data-splide]):not(.js-native-product-carousel)')
    .forEach((el) => {
      if (options.skipDeferred && el.closest('[data-splide-defer]')) return;
      if (el.dataset.splideInitialized === 'true') return;

      const list = el.querySelector('.splide__list');
      if (!list) return;
      if (list.querySelectorAll('.splide__slide').length === 0) return;

      el.dataset.splideInitialized = 'true';

      new Splide(el, {
        mediaQuery: 'min',
        breakpoints: {
          1024: {
            type: 'slide',
            snap: true,
            perPage: 4,
            perMove: 4,
            pagination: true,
          },
        },
        perPage: 1,
        perMove: 1,
        arrows: false,
        gap: '5px',
      }).mount();
    });
}

function initSplides(root = document, options = {}) {
  initNativeProductCarousels(root, options);
  initClassicProductSplides(root, options);
}

window.initSplides = initSplides;

/**
 * Mount carousels that declare their own data-splide JSON
 * (product gallery, homepage image carrousel, etc.).
 * Default arrows off — matches pre-existing theme behaviour.
 */
function mountSplideElementsWithDataConfig(root = document, options = {}) {
  if (typeof Splide === 'undefined') return;

  root.querySelectorAll('.splide[data-splide]').forEach((el) => {
    if (options.skipDeferred && el.closest('[data-splide-defer]')) return;
    if (el.dataset.splideInitialized === 'true') return;

    const list = el.querySelector('.splide__list');
    if (!list || list.querySelectorAll('.splide__slide').length === 0) return;

    let parsed = {};
    try {
      parsed = JSON.parse(el.getAttribute('data-splide'));
    } catch (e) {
      parsed = {};
    }

    const splideOptions = Object.assign({ arrows: false }, parsed);
    if (splideOptions.breakpoints) {
      Object.keys(splideOptions.breakpoints).forEach((bp) => {
        const conf = splideOptions.breakpoints[bp];
        if (conf && conf.destroy !== true && conf.arrows === undefined) {
          conf.arrows = false;
        }
      });
    }

    el.dataset.splideInitialized = 'true';
    const splide = new Splide(el, splideOptions);
    el.splide = splide;
    splide.mount();

    // Ensure overflow/pagination state is correct after layout
    requestAnimationFrame(() => {
      try {
        splide.refresh();
      } catch (e) {
        /* ignore */
      }
    });
  });
}

window.mountSplideElementsWithDataConfig = mountSplideElementsWithDataConfig;

function runSplideInits(root = document, options = {}) {
  initSplides(root, options);
  mountSplideElementsWithDataConfig(root, options);
}

window.runSplideInits = runSplideInits;

function bootSplide() {
  runSplideInits(document, { skipDeferred: true });
  setTimeout(() => runSplideInits(document, { skipDeferred: true }), 1500);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootSplide);
} else {
  bootSplide();
}

document.addEventListener('shopify:section:load', (event) => {
  runSplideInits(event.target);
});
